/**
 * Locale formatting and parsing for monetary amounts.
 *
 * These are the ONLY locale-aware calls in the amount field, and they touch the
 * displayed string exclusively — the value the form holds (and the backend
 * receives) is a plain `number`, which carries no locale. Keeping them here,
 * outside the component, is what makes that separation testable.
 */

/**
 * The group and decimal separators the locale actually uses. Derived rather
 * than hardcoded so parsing round-trips whatever `formatAmount` produced —
 * including the narrow no-break space fr-FR groups with and the non-standard
 * grouping positions of en-IN.
 */
export function separatorsFor(locale?: string): {
  group: string
  decimal: string
} {
  const parts = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
  }).formatToParts(12345.6)
  return {
    group: parts.find((p) => p.type === "group")?.value ?? ",",
    decimal: parts.find((p) => p.type === "decimal")?.value ?? ".",
  }
}

/** The grouped, fixed-precision string shown while the field is not focused. */
export function formatAmount(
  value: number | null | undefined,
  locale?: string,
  decimals = 2,
): string {
  if (value == null || !Number.isFinite(value)) return ""
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * The ungrouped, locale-punctuated string a person EDITS — shown on focus.
 * Full precision, so focusing a value that carries more digits than the display
 * shows reveals them instead of letting the next keystroke truncate them.
 */
export function rawAmount(
  value: number | null | undefined,
  locale?: string,
): string {
  if (value == null || !Number.isFinite(value)) return ""
  return new Intl.NumberFormat(locale, {
    useGrouping: false,
    maximumFractionDigits: 20,
  }).format(value)
}

/** Every space a locale may group digits with (fr-FR uses U+202F). */
const SPACES = /[\s   ]/g

/**
 * A locale-written amount → a number. Returns `null` for empty or unparseable
 * input, so an optional amount stays genuinely unset rather than snapping to 0.
 */
export function parseAmount(
  input: string | null | undefined,
  locale?: string,
): number | null {
  if (input == null) return null
  const { group, decimal } = separatorsFor(locale)
  const canonical = input
    .split(group)
    .join("")
    .replace(SPACES, "")
    .split(decimal)
    .join(".")
    .replace(/[^0-9.-]/g, "")
  if (canonical === "" || canonical === "-" || canonical === ".") return null
  const parsed = Number(canonical)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Rounds to a fixed number of decimal places — the field's `scale`, i.e. the
 * most the stored value may carry. The epsilon nudge keeps the usual binary
 * representation cases (1.005 → 1.01) from rounding down.
 */
export function roundToScale(value: number, scale: number): number {
  const factor = 10 ** scale
  return Math.round((value + Number.EPSILON) * factor) / factor
}

/**
 * Keeps a partially-typed string within `scale` decimal places, in DISPLAY
 * space (the locale's separator), so the cap applies as the user types rather
 * than surprising them on blur.
 */
export function capDecimals(
  text: string,
  decimal: string,
  scale: number,
): string {
  const at = text.indexOf(decimal)
  if (at === -1) return text
  return scale === 0 ? text.slice(0, at) : text.slice(0, at + 1 + scale)
}
